import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo } from "react";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine } from "@ui/IconV2/react";
import Switch from "@ui/Switch";
import { colors, spacings } from "@ui/Theme";
import InputV2 from "@ui/Input/InputV2";
import type { BlockPlotModelT } from "../../../../../../../schema/block/plot";
import t from "../../../../../../../../../../i18n";
import { LayerEncoding } from "../LayerEncoding";
import {
  useLayerClearZoomCallback,
  useLayerUpdateZoomMax,
  useLayerUpdateZoomMin,
} from "../../hook/useLayerUpdateZoom";
import { useLayerRemoveEncodingCallback } from "../../hook/useLayerRemoveEncodingCallback";
import { useLayerClearRuleCallback, useLayerRule } from "../../hook/useLayerRules";
import { formSectionItemFull } from "../../_styles/form";
import { useLayerRulePlotbands } from "../../hook/useLayerRulePlotbands";
import TooltipV2 from "@ui/TooltipV2";

const layerSettingDetailHeader = css`
  border-left: 1px solid ${colors.titanium200};
  grid-template-columns: 0px auto;
  display: grid;
  gap: 0;
  width: 100%;
  overflow: hidden;
  padding-left: ${spacings.xs};
  transition:
    grid-template-columns var(--section-column-transition-duration)
      var(--section-column-transition-ease),
    gap var(--section-column-transition-duration) var(--section-column-transition-ease);

  > :nth-child(1) {
    grid-column: 2 / span 1;
  }
`;

const layerSettingContainer = css`
  align-items: center;
  display: grid;
  grid-template-columns: 1fr max-content;

  & + & {
    margin-top: 0px;

    .${layerSettingDetailHeader} {
      padding-top: ${spacings.s};
    }
  }
`;
const layerSettingZoomToggle = css`
  grid-column: 1 / span 2;
`;

const layerSettingActions = css`
  grid-column: 2 / span 1;
  grid-row: 1 / span 1;
  display: flex;
  justify-content: space-around;
`;

const layerSettingSideBySideInputs = css`
  display: grid;
  grid-column: 1 / span 2;
  grid-template-columns: 1fr 1fr;
  /* flex-wrap: wrap; */
  column-gap: ${spacings.s};
  row-gap: ${spacings.xs};
`;

const overtakingPlotBandsSwitch = css`
  grid-column: 1 / span 2;
`;

const layerSettingZoom = css`
  margin-top: ${spacings.s};
`;

const layerSettingRules = css`
  border-left: 1px solid ${colors.titanium200};
  padding-left: ${spacings.xs};
  padding-top: ${spacings.xs};
`;

export const LayerSetting = memo(
  ({
    projectId,
    encoding,
    axis,
    position,
    displayZoomOptions = true,
    preset,
  }: {
    projectId: string;
    encoding: BlockPlotModelT["config"]["layers"][number]["encoding"]["x" | "y"];
    axis: "x" | "y";
    position: number;
    preset: "line" | "scatter";
    displayZoomOptions?: boolean;
  }) => {
    /** Encodings */
    const layerRemoveEncoding = useLayerRemoveEncodingCallback(position, axis);

    /** Zoom */
    const { withZoomOptions, toggleZoomOptions } = useLayerClearZoomCallback(position, axis);
    const { zoomMin, layerUpdateZoomMin } = useLayerUpdateZoomMin(position, axis);
    const { zoomMax, layerUpdateZoomMax } = useLayerUpdateZoomMax(position, axis);

    /** Rules */
    const { withRuleOptions, toggleRuleOptions } = useLayerClearRuleCallback(position, axis);
    const { value: ruleMin, updateLayerRule: updateLayerRuleMin } = useLayerRule(position, axis, 0);
    const { value: ruleMax, updateLayerRule: updateLayerRuleMax } = useLayerRule(position, axis, 1);
    const { value: showOvertakingPlotbands, updateOvertakingPlotbands } = useLayerRulePlotbands();

    return (
      <div className={cx(layerSettingContainer)}>
        <header className={cx(layerSettingDetailHeader)}>
          <LayerEncoding projectId={projectId} layer={position} encoding={encoding} />
        </header>
        <div className={cx(layerSettingActions)}>
          <TooltipV2 overlay={t("tooltip.editor.plot.delete_encoding")}>
            <ButtonIconV2 onClick={layerRemoveEncoding}>
              <DeleteBinLine size="s" />
            </ButtonIconV2>
          </TooltipV2>
        </div>
        <div className={cx(layerSettingRules, layerSettingSideBySideInputs)}>
          <Switch
            checked={withRuleOptions}
            onChange={e => toggleRuleOptions(e)}
            className={formSectionItemFull}
            position="right"
            justify
          >
            {t("project.dashboard.editor.gui.section.rule.title")}
          </Switch>
          {withRuleOptions && (
            <>
              <InputV2
                type="number"
                label={t("project.dashboard.editor.gui.section.rule.min.label")}
                value={ruleMin ?? ""}
                onChange={e => updateLayerRuleMin(e.target.value, "min")}
              />
              <InputV2
                type="number"
                label={t("project.dashboard.editor.gui.section.rule.max.label")}
                value={ruleMax ?? ""}
                onChange={e => updateLayerRuleMax(e.target.value, "max")}
              />
              {preset === "line" && (
                <Switch
                  className={overtakingPlotBandsSwitch}
                  checked={showOvertakingPlotbands}
                  onChange={e => updateOvertakingPlotbands(e.target.checked)}
                >
                  {t("project.dashboard.editor.gui.section.rule.plot_bands")}
                </Switch>
              )}
            </>
          )}
        </div>

        {displayZoomOptions && (
          <div className={cx(layerSettingZoom, layerSettingSideBySideInputs)}>
            <Switch
              checked={withZoomOptions}
              onChange={e => toggleZoomOptions(e)}
              className={cx(layerSettingZoomToggle)}
              position="right"
              justify
            >
              {t("project.dashboard.editor.gui.section.encoding.zoom.title")}
            </Switch>
            {withZoomOptions && (
              <div className={cx(layerSettingSideBySideInputs)}>
                <InputV2
                  type="number"
                  //   placeholder={t("project.dashboard.editor.gui.section.encoding.zoom.min.placeholder")}
                  label={t("project.dashboard.editor.gui.section.encoding.zoom.min.label")}
                  value={zoomMin ?? undefined}
                  onChange={e => layerUpdateZoomMin(e.target.value)}
                />
                <InputV2
                  type="number"
                  //   placeholder={t("project.dashboard.editor.gui.section.encoding.zoom.max.placeholder")}
                  label={t("project.dashboard.editor.gui.section.encoding.zoom.max.label")}
                  value={zoomMax ?? undefined}
                  onChange={e => layerUpdateZoomMax(e.target.value)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
